/* eslint-disable react/require-default-props */
/* eslint-disable no-trailing-spaces */
/**
 * Copyright (c) 2021 Smartlife Labs
 * Size Selector
 *
 * @project : smartlife-pro2-web
 * @createdDate : Mon Feb 15 2021
 * @author : Mayantha Jayawardena
 * -----
 * @lastModified : Tue Nov 30 2021
 * @modifiedBy : Mayantha Jayawardena
 * -----
 * HISTORY:
 * Date      By              Comments
 * --------- --------------- ---------------------------------------------------------
 * 30-11-2021 MayanthaJ      Pro device size selector added
 */

import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { ITEM_DEFAULTS } from '../../../config/deviceConfig/item_defaults';
import { ProDeviceFieldTextInput } from './DeviceFeatures/TextInput/TextInput';
import { ProDeviceFieldTextInputFeature } from './DeviceFeatures/TextInput/TextInputFeature';
import { ProDeviceFieldStreamingServerInput } from './DeviceFeatures/StreamingServerInput/StreamingServerInput';
import { ProDeviceFieldStateCheckbox } from './DeviceFeatures/StateCheckbox/StateCheckbox';
import { ProDeviceFieldCustomeSelector } from './DeviceFeatures/CustomSelector/CustomSelector';
import { ProDeviceFieldSoundVolumeStep } from './DeviceFeatures/SoundVolume/SoundVolumeStep';
import { ProDeviceFieldSoundVolume } from './DeviceFeatures/SoundVolume/SoundVolume';
import { ProDeviceFieldNetworkPort } from './DeviceFeatures/Network/NetworkPort';
import { ProDeviceFieldIpAddress } from './DeviceFeatures/Network/IpAddress';
import { ProDeviceFieldRelayValues } from './DeviceFeatures/RelayValues/RelayValues';
import { ProDeviceFieldDisplayLabels } from './DeviceFeatures/DisplayLabels/DisplayLabels';
import { ProDeviceFieldLabelViewTypeSelector } from './DeviceFeatures/DisplayLabels/DisplayLabelViewType';
import { ProDeviceFieldFieldSelector } from './DeviceFeatures/FieldSelector/FieldSelector';
import { ProDeviceFieldInterval } from './DeviceFeatures/Interval/Interval';
import { ProDeviceFieldTimeout } from './DeviceFeatures/Timeout/Timeout';
import { ProDeviceFieldDebounce } from './DeviceFeatures/debounce/debounce';
import { ProDeviceFieldPinCommon } from './DeviceFeatures/pins/PinCommon';
import { ProDeviceFieldPinUnique } from './DeviceFeatures/pins/PinUnique';
import { ProDeviceFieldRemoteSelector } from './DeviceFeatures/pins/RemoteSelector';
import { ProDeviceFieldZones } from './DeviceFeatures/ZoneSource/ZonesSelector';
import { ProDeviceFieldZoneModalSelector } from './DeviceFeatures/ZoneSource/zsModalSelector';
import { IFormResetRef } from '../ProDeviceCreateEdit/types';
import { IDeviceCategory } from '../../../config/deviceConfig/types';
import { ProDeviceFieldJsonField } from './DeviceFeatures/JsonField/JsonField';
import { ProDeviceFieldFieldEnableCard } from './DeviceFeatures/FieldEnableCard/FieldEnableCard';
import './DybanucDeviceCreate.css';
import { appConsoleError } from '../../../utility/appUtils';
import { DeviceCollectionType } from '../../../components/UserViewComponents/UIItems/UVDeviceFinder/types';
import { ProDeviceFielCustomValidator1 } from './DeviceFeatures/CustomValidator1/CustomValidator1';
import { ProDeviceFieldLocationVariable } from './DeviceFeatures/LocationVariable/LocationVariable';
import { ProDeviceFieldTimeoutInterval } from './DeviceFeatures/TimeoutInterval/TimeoutInterval';
import { ProDeviceFieldSizeModalSelector } from './DeviceFeatures/SizeSelector/SizeModalSelector';

interface ComponentProps {
  // eslint-disable-next-line react/no-unused-prop-types
  deviceCollectionType?: string;
  keyPrefix: number;
  cat: IDeviceCategory;
  formValidate: number;
  deviceInfo: any;
  resetRef: IFormResetRef;
  detectPayloadPush: (
    payload: any,
    isInitializing?: boolean,
    flushInitialDataToState?: boolean
  ) => void;
}

type DynamicDeviceFieldType = {
  cat: any;
  field: any;
  fieldKey: any;
  formValidate: any;
  resetRef: any;
  deviceInfo: any;
  detectPayloadPush: (
    payload: any,
    isInitializing?: boolean,
  ) => void;
};

// eslint-disable-next-line import/prefer-default-export
export const DynamicDeviceCreate: React.FunctionComponent<DynamicDeviceFieldType> = ({
  cat,
  field,
  fieldKey,
  formValidate,
  resetRef,
  deviceInfo,
  detectPayloadPush,
}) => {
  // return <div className="row">{renderItems.length === 0 ? <p /> : renderItems}</div>;
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.json_field) {
    return <ProDeviceFieldJsonField key={fieldKey} fieldConfig={field} deviceInfo={deviceInfo} />;
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.location_variable) {
    return (
      <ProDeviceFieldLocationVariable
        key={fieldKey}
        cat={cat}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.text_input) {
    return (
      <ProDeviceFieldTextInput
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.text_input_feature) {
    return (
      <ProDeviceFieldTextInputFeature
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.stream_server) {
    return (
      <ProDeviceFieldStreamingServerInput
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.state_checkbox) {
    return (
      <ProDeviceFieldStateCheckbox
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.custom_selector) {
    return (
      <ProDeviceFieldCustomeSelector
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.sound_volume_step) {
    return (
      <ProDeviceFieldSoundVolumeStep
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.sound_volume) {
    return (
      <ProDeviceFieldSoundVolume
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.network_port) {
    return (
      <ProDeviceFieldNetworkPort
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.ip_address) {
    return (
      <ProDeviceFieldIpAddress
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.relay_value) {
    return (
      <ProDeviceFieldRelayValues
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.display_labels) {
    return (
      <ProDeviceFieldDisplayLabels
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.display_labels_view_type) {
    return (
      <ProDeviceFieldLabelViewTypeSelector
        key={fieldKey}
        validateForm={formValidate}
        fieldConfig={field}
        resetForm={resetRef}
        deviceInfo={deviceInfo}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.field_selector) {
    return (
      <ProDeviceFieldFieldSelector
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.interval) {
    return (
      <ProDeviceFieldInterval
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.timeout) {
    return (
      <ProDeviceFieldTimeout
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.timeout_interval) {
    return (
      <ProDeviceFieldTimeoutInterval
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.debounce) {
    return (
      <ProDeviceFieldDebounce
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.pin_common) {
    return (
      <ProDeviceFieldPinCommon
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.pin_unique) {
    return (
      <ProDeviceFieldPinUnique
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.zones) {
    // console.log('wwwwwwww', field, deviceInfo);
    return (
      <Paper style={{ marginBottom: '10px' }} elevation={1} key={fieldKey}>
        <ProDeviceFieldZones
          fieldConfig={field}
          validateForm={formValidate}
          deviceInfo={deviceInfo}
          resetForm={resetRef}
          onChange={(payload: any, isInitializing) => {
            detectPayloadPush(payload, isInitializing);
          }}
        />
      </Paper>
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.zs_modal_selector) {
    return (
      <ProDeviceFieldZoneModalSelector
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.ah_size_selector) {
    return (
      <ProDeviceFieldSizeModalSelector
        key={fieldKey}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  if (field.type === ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.ir_remote) {
    return (
      <ProDeviceFieldRemoteSelector
        key={fieldKey}
        cat={cat}
        fieldConfig={field}
        validateForm={formValidate}
        deviceInfo={deviceInfo}
        resetForm={resetRef}
        onChange={(payload: any, isInitializing) => {
          detectPayloadPush(payload, isInitializing);
        }}
      />
    );
  }
  appConsoleError('Device Field type not found', field.type);
  return <></>;
};

// eslint-disable-next-line import/prefer-default-export
export const DynamicDeviceCreateContainer: React.FunctionComponent<ComponentProps> = ({
  cat,
  keyPrefix,
  formValidate,
  deviceInfo,
  resetRef,
  deviceCollectionType,
  detectPayloadPush,
}) => {
  const [fields, setFields] = useState<{[key: string]: any}>({});

  useEffect(() => {
    if (cat.isItem && cat.config != null && cat.config.features) {
      if (cat.config.features.fields && cat.config.features.fields.length > 0) {
        const { fields: fieldsFromConfig } = cat.config.features;
        setFields(fieldsFromConfig);
      }
    }
  }, [cat]);

  return (
    <>
      {Object.keys(fields).map((key, i) => {
        const field = fields[key];
        const fieldKey = `${keyPrefix}_${i}_${field.field}${cat.subCategory}`;

        if (deviceCollectionType === DeviceCollectionType.airDevice) {
          if (
            ITEM_DEFAULTS.ITEM_CREATOR.AIR_DEVICE_FIELDS_CAN_REMOVE.includes('all')
            || ITEM_DEFAULTS.ITEM_CREATOR.AIR_DEVICE_FIELDS_CAN_REMOVE.indexOf(field.type) > -1
          ) {
            return (
              <ProDeviceFieldFieldEnableCard
                fieldTypeName={field.type}
                key={fieldKey}
                fieldConfig={field}
                validateForm={formValidate}
                deviceInfo={deviceInfo}
                resetForm={resetRef}
                onRemove={(payload: any) => {
                  detectPayloadPush(payload);
                }}
              >
                <DynamicDeviceCreate
                  field={field}
                  cat={cat}
                  resetRef={resetRef}
                  deviceInfo={deviceInfo}
                  fieldKey={fieldKey}
                  formValidate={formValidate}
                  detectPayloadPush={(payload, isInitializing) => {
                    if (isInitializing && i === Object.keys(fields).length - 1) {
                      detectPayloadPush(payload, true, true);
                    } else if (isInitializing) {
                      detectPayloadPush(payload, true, false);
                    } else {
                      detectPayloadPush(payload);
                    }
                  }}
                />
              </ProDeviceFieldFieldEnableCard>
            );
          }
        }
        return (
          <DynamicDeviceCreate
            field={field}
            cat={cat}
            resetRef={resetRef}
            deviceInfo={deviceInfo}
            fieldKey={fieldKey}
            formValidate={formValidate}
            detectPayloadPush={(payload, isInitializing) => {
              if (isInitializing && i === Object.keys(fields).length - 1) {
                detectPayloadPush(payload, true, true);
              } else if (isInitializing) {
                detectPayloadPush(payload, true, false);
              } else {
                detectPayloadPush(payload);
              }
            }}
          />
        );
      })}
    </>
  );
};
